import { useEffect, useState } from 'react'

import { useMergeCardsWithDeckCards } from 'components/customCards/CustomCardListOverlay/useMergeCardsWithDeckCards'

import SaveService from 'services/save.service'
import CustomCardService from 'services/customCards.service'
import ToastService from 'services/toast.service'

import { CustomCard } from 'services/apiTypes/customCards.types'
import { CardType } from 'types/deck'

import PhatInput from 'components/formElements/PhatInput'
import PhatButton from 'components/formElements/PhatButton'
import LineLoader from 'components/elements/LineLoader'
import GlobalOverlayStack from 'components/elements/Overlay/GlobalOverlayStack'
import CustomCardImageCard from 'components/customCards/CustomCardImageCard'
import ImageCard from 'components/card/deckCards/imageCard'
import Link from 'components/elements/Link'
import CustomCardsOrderByDropdown from 'components/customCards/CustomCardsOrderByDropdown'

import styles from './customCardListOverlay.module.scss'

type Props = {
  open: boolean
  onClose: () => void
}

const CustomCardListOverlay = ({ open, onClose }: Props) => {
  const [loading, setLoading] = useState(true)
  const [customCards, setCustomCards] = useState(new Array<CustomCard>())
  const [textFilter, setTextFilter] = useState('')
  const [orderBy, setOrderBy] = useState({ orderBy: 'name', orderDirection: 'ascending' })
  const [nextUrl, setNextUrl] = useState<string | null>(null)

  const cards = useMergeCardsWithDeckCards(customCards)

  const handleUpdateCustomCard = (updatedCard: CustomCard) => {
    const cardIndex = customCards.findIndex(customCard => customCard.id === updatedCard.id)

    if (cardIndex < 0) return // Wut

    const updatedCustomCards = [...customCards]

    updatedCustomCards[cardIndex] = updatedCard

    setCustomCards(updatedCustomCards)
  }

  const handleAddCardToDeckState = (card: CardType) => {
    return (
      SaveService.save({ ...card, qty: 1 })
        // pretter-ignore
        .catch(() => ToastService.create('Unabled to add card to deck', 'Save service', 'error'))
    )
  }

  useEffect(() => {
    if (!open) {
      setLoading(true)
      setCustomCards([])

      return
    }

    handleFetchCustomCards()
  }, [open])

  const handleFetchCustomCards = (searchParams: Record<string, any> = {}, nextUrl?: string) => {
    setLoading(true)

    return CustomCardService.list(searchParams, nextUrl)
      .then(res => {
        // IF it was a next page request, append the results to the end rather than clearing them out
        if (nextUrl) setCustomCards([...customCards, ...res.results])
        else setCustomCards(res.results)

        setNextUrl(res.next)
      })
      .catch(err => ToastService.create(err, 'Custom Card Service', 'error'))
      .finally(() => setLoading(false))
  }

  const handleFilterCustomCards = (e: React.FormEvent) => {
    e.preventDefault()

    handleFetchCustomCards({ search: textFilter, ...orderBy })
  }

  return (
    <GlobalOverlayStack open={open} onClose={onClose} className={styles.container}>
      <h2>Custom cards</h2>

      <div className={styles.controls}>
        <form onSubmit={handleFilterCustomCards} className={styles.filterForm}>
          <PhatInput
            header="Full text search"
            placeholder="Card name or text box"
            value={textFilter}
            onChange={setTextFilter}
          />

          <CustomCardsOrderByDropdown
            onChange={(orderBy, orderDirection) => setOrderBy({ orderBy, orderDirection })}
            callBackOnDirectionChange
          />

          <PhatButton icon="search" color="green" type="submit" />
        </form>

        <Link href="/custom-cards">
          <PhatButton color="blue" icon="circle-plus">
            Create custom card
          </PhatButton>
        </Link>
      </div>

      {loading && <LineLoader />}

      {!loading && !customCards.length && <div>No custom cards found</div>}

      <div className={styles.cards}>
        {cards.map((card, index) => {
          if (!card.qty)
            return (
              <CustomCardImageCard
                key={`${index}-${card.customCard?.id}`}
                card={card}
                onCardEdited={handleUpdateCustomCard}
                onAddToDeckState={handleAddCardToDeckState}
                noExtrasMenu
              />
            )

          // Replace with a card that is actually in the deck if the card is already in the deck
          return <ImageCard key={card.id} cardId={card.id} hidePrices />
        })}
      </div>

      {nextUrl && <PhatButton onClick={() => handleFetchCustomCards({}, nextUrl)}>Load more</PhatButton>}
      {nextUrl && loading && <LineLoader />}
    </GlobalOverlayStack>
  )
}

export default CustomCardListOverlay
